var __read = (this && this.__read) || function (o, n) {
    var m = typeof Symbol === "function" && o[Symbol.iterator];
    if (!m) return o;
    var i = m.call(o), r, ar = [], e;
    try {
        while ((n === void 0 || n-- > 0) && !(r = i.next()).done) ar.push(r.value);
    }
    catch (error) { e = { error: error }; }
    finally {
        try {
            if (r && !r.done && (m = i["return"])) m.call(i);
        }
        finally { if (e) throw e.error; }
    }
    return ar;
};
import { jsx as _jsx, Fragment as _Fragment } from "@emotion/react/jsx-runtime";
import { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useAppDispatch, actions, selectors, useAppSelector } from "store";
import { KeyBindings, useKeyBindings } from "hooks/keybindings";
import { useNumberConversion } from "hooks/number-conversion";
import { Unit } from "utility/number";
import { SidebarDrawerButton } from "ui/components/elements/custom-drawer";
import { ComponentSection } from "ui/components/panels/canvas/node-components/component-select/component-section";
import { ComponentSelect } from "ui/components/panels/canvas/node-components/component-select/component-select";
import { systemDataConstants } from "api/system-data";
var supportedKeyboardKeys = [KeyBindings.DELETE, KeyBindings.ESCAPE];
export var CentralizedVacuumPumpMenu = function (_a) {
    var nodes = _a.nodes;
    var _b = __read(useState(false), 2), drawerOpened = _b[0], setDrawerOpened = _b[1];
    var t = useTranslation().t;
    var dispatch = useAppDispatch();
    var systemDataDefineParameters = useAppSelector(selectors.selectSystemDataDefineParameters);
    useKeyBindings({ node: nodes[0], keys: supportedKeyboardKeys });
    var convertNumber = useNumberConversion();
    var nodeIds = nodes.map(function (node) { return node.id; });
    var onSelectPump = useCallback(function (pump) {
        dispatch(actions.selectCentralPump({
            nodeIds: nodeIds,
            value: pump,
        }));
    }, [dispatch, nodeIds]);
    var currentlySelectedPumpValues = useAppSelector(selectors.selectSelectedCentralPumps(nodeIds)).map(function (pump) { return pump === null || pump === void 0 ? void 0 : pump.value; });
    var pumpOptions = useMemo(function () {
        if (!(systemDataDefineParameters === null || systemDataDefineParameters === void 0 ? void 0 : systemDataDefineParameters.vacuumPumpComponents))
            return [];
        return Object.values(systemDataDefineParameters.vacuumPumpComponents).filter(function (component) {
            return (component.valid ||
                currentlySelectedPumpValues.includes(component.value)) &&
                /* currentlySelectedPump will be undefined if there has been an error and no pump
                 * has been successfully committed to Tacton. This is the only time we want to show the
                 * "no pump selected" option */
                (!currentlySelectedPumpValues.length ||
                    currentlySelectedPumpValues.every(function (pump) { return pump === undefined; }) ||
                    component.value !==
                        systemDataConstants.parameterValues.noPumpSelected);
        });
    }, [
        currentlySelectedPumpValues,
        systemDataDefineParameters === null || systemDataDefineParameters === void 0 ? void 0 : systemDataDefineParameters.vacuumPumpComponents,
    ]);
    var createLabel = useCallback(function (component) {
        return component.value === systemDataConstants.parameterValues.noPumpSelected
            ? component.label
            : component.label +
                " (rec ".concat(convertNumber(Number(component.recFeedPressureMpa), Unit.MPA)
                    .formatted, ")");
    }, [convertNumber]);
    var formattedItemLabels = useMemo(function () { return pumpOptions.map(createLabel); }, [createLabel, pumpOptions]);
    var selectedLabels = useMemo(function () {
        return currentlySelectedPumpValues
            .map(function (value) { return pumpOptions.find(function (option) { return option.value === value; }); })
            .filter(function (option) { return option !== undefined; }).map(createLabel);
    }, [createLabel, currentlySelectedPumpValues, pumpOptions]);
    var values = useMemo(function () { return pumpOptions.map(function (option) { return option.value; }); }, [pumpOptions]);
    var selectedValues = useMemo(function () {
        return currentlySelectedPumpValues.filter(function (option) { return option !== undefined; });
    }, [currentlySelectedPumpValues]);
    return (_jsx(_Fragment, { children: pumpOptions && (_jsx(SidebarDrawerButton, { button: _jsx(ComponentSection, { header: t("sidebar.edit_pump.pump_dropdown_label"), selected: drawerOpened, onClick: function () { return setDrawerOpened(!drawerOpened); }, value: selectedLabels.length > 0 ? selectedLabels[0] : undefined }), id: "centralized-vacuum-pump-drawer", sidebarWidth: 322, customRenderElement: _jsx(ComponentSelect, { labels: formattedItemLabels, values: values, selectedValues: selectedValues, onValueChange: onSelectPump, scrollIntoPosition: "nearest" }) })) }));
};
